import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { GlobalErrorHandlerService } from './global-error-handler.service';
import { BrandingService } from '../branding.service';
import { UserService } from '../user.service';
import axios from 'axios';
import { AZURE_MONITOR_CONFIG, AzureMonitorConfig } from 'src/app/config/AppInitializer.factory';

@Injectable({
  providedIn: 'root'
})
export class TelemetryService {
  private readonly TAG_AI_CLOUD_ROLE_KEY = 'ai.cloud.role';
  private readonly TAG_AI_USER_ID_KEY = 'ai.user.id';

  private readonly HTTP_HEADER_REQUEST_ID = 'X-MS-Request-ID';
  private readonly HTTP_HEADER_CORRELATION_ID = 'X-MS-Correlation-Request-ID';

  private angularPlugin = new AngularPlugin();
  private applicationInsights: ApplicationInsights | undefined;

  constructor(
    private _userService: UserService,
    private _brandingService: BrandingService,
    @Inject(AZURE_MONITOR_CONFIG) private azureMonitorConfig: AzureMonitorConfig,
    private _globalErrorHandlerService: GlobalErrorHandlerService,
    private _router: Router) {
      this.initializeApplicationInsights();
  }

  private async initializeApplicationInsights() {
    const connectionString = this.azureMonitorConfig.connectionString;
    // TODO: Silently fail until we have a review how to handle application insights for the UI without 
    // exposing the connection string in client side code. The backend will send an empty connection string for now.
    if (!connectionString) {
      return;
    }

    this.applicationInsights = new ApplicationInsights({
      config: {
        connectionString,
        extensions: [this.angularPlugin],
        extensionConfig: {
          [this.angularPlugin.identifier]: {
              router: this._router,
              errorServices: [this._globalErrorHandlerService],
           }
        },
        disableCorrelationHeaders: false,
        // unload is blocked by Chrome Permissions Policy; use visibilitychange instead
        onunloadDisableBeacon: false,
        disablePageUnloadEvents: ['unload']
      }
    });

    this.applicationInsights.addTelemetryInitializer((envelope: ITelemetryItem) => {
      if (!envelope)
      {
        return;
      }

      envelope.tags ??= {};
      envelope.tags[this.TAG_AI_CLOUD_ROLE_KEY] = this._brandingService.applicationName;
      envelope.tags[this.TAG_AI_USER_ID_KEY] = this._userService.getUsername();
    });

    axios.interceptors.request.use((config) => {
      const operationId = this.applicationInsights?.context?.telemetryTrace?.traceID;
      if (operationId) {
        const headers = (config.headers ??= {} as any);
        headers[this.HTTP_HEADER_REQUEST_ID] = operationId;
        headers[this.HTTP_HEADER_CORRELATION_ID] = operationId;
      }

      return config;
    });

    this.applicationInsights.loadAppInsights();
  }

  logPageView(name?: string, url?: string) {
    this.applicationInsights?.trackPageView({ name, uri: url });
  }

  logEvent(name: string, properties?: { [key: string]: any }) {
    this.applicationInsights?.trackEvent({ name }, properties);
  }

  logException(exception: Error, severityLevel?: number) {
    this.applicationInsights?.trackException({ exception, severityLevel });
  }

  logTrace(message: string, properties?: { [key: string]: any }) {
    this.applicationInsights?.trackTrace({ message }, properties);
  }

  logMetric(name: string, average: number, properties?: { [key: string]: any }) {
    this.applicationInsights?.trackMetric({ name, average }, properties);
  }
}
